import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Code2, Smartphone, Server, Cloud, Palette,
  ShieldCheck, Database, LineChart, type LucideIcon,
} from 'lucide-react';
import { useLang, type Lang } from '../../../i18n';
import { get } from '../../../services/api';
import useIntersectionFadeIn from '../../../hooks/useIntersectionFadeIn';

interface ServiceCategory { slug: string; title: string; description?: string; icon_key?: string }
interface CategoriesApi { items?: ServiceCategory[]; results?: ServiceCategory[] }

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

const ICONS: Record<string, LucideIcon> = {
  web: Code2, mobile: Smartphone, enterprise: Server, devops: Cloud,
  design: Palette, security: ShieldCheck, data: Database, cloud: LineChart,
  consulting: LineChart, ai: Database, automation: Server,
  ecm: Server, communications: Smartphone, integration: Cloud,
};

export default function ServicesGridSection() {
  const { t, lang } = useLang();
  const [items, setItems] = useState<ServiceCategory[]>([]);
  const rootRef = useRef<HTMLElement>(null);
  const visible = useIntersectionFadeIn(rootRef);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<CategoriesApi>(`/services/categories/?lang=${LANG_MAP[lang]}`);
        if (cancel) return;
        const list = res?.items || res?.results || [];
        setItems(list.slice(0, 9));
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  if (!items.length) return null;

  return (
    <section ref={rootRef} className={`section services scroll-fade-in${visible ? ' scroll-fade-in--visible' : ''}`}>
      <div className="container">
        <header className="section-header">
          <span className="eyebrow">{t('home.services.eyebrow')}</span>
          <h2>{t('home.services.title')}</h2>
          <p className="lead">{t('home.services.sub')}</p>
        </header>

        <div className="services__grid">
          {items.map((s, idx) => {
            const Icon = ICONS[s.icon_key || s.slug] || Code2;
            const num = String(idx + 1).padStart(2, '0');
            return (
              <Link key={s.slug} to={`/services?category=${s.slug}`} className="services__card card-frame card-hover">
                <div className="services__head">
                  <span className="services__num">{num}</span>
                  <span className="services__icon" aria-hidden="true">
                    <Icon size={24} strokeWidth={1.75} />
                  </span>
                </div>
                <h3 className="services__title">{s.title}</h3>
                {s.description ? <p className="services__desc">{s.description}</p> : null}
                <span className="services__more">
                  {t('home.services.more')}
                  <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
